import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Camera, Upload, ArrowRight, Sparkles, Shield, Download, Share2, CheckCircle2 } from 'lucide-react';
interface KnightingCeremonyProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (photoUrl: string) => void;
}
type Step = 'intro' | 'capture' | 'oath' | 'reveal' | 'share';
export const KnightingCeremony: React.FC<KnightingCeremonyProps> = ({
  isOpen,
  onClose,
  onComplete
}) => {
  const [step, setStep] = useState<Step>('intro');
  const [gender, setGender] = useState<'male' | 'female' | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [captureMode, setCaptureMode] = useState<'choose' | 'camera'>('choose');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };
  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: {
            ideal: 1280
          },
          height: {
            ideal: 1280
          }
        },
        audio: false
      });
      streamRef.current = stream;
      setCaptureMode('camera');
      // Defer attaching to next tick so video element is rendered
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 50);
    } catch (err) {
      setCameraError('Could not access camera. Please allow camera permission or upload a photo instead.');
      setCaptureMode('choose');
    }
  };
  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    const size = Math.min(video.videoWidth, video.videoHeight);
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const sx = (video.videoWidth - size) / 2;
    const sy = (video.videoHeight - size) / 2;
    // Mirror the image horizontally (selfie style)
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    setPhoto(dataUrl);
    stopCamera();
    setCaptureMode('choose');
  };
  const cancelCamera = () => {
    stopCamera();
    setCaptureMode('choose');
  };
  // Cleanup camera on unmount or modal close
  useEffect(() => {
    if (!isOpen) {
      stopCamera();
      setCaptureMode('choose');
    }
    return () => stopCamera();
  }, [isOpen]);
  if (!isOpen) return null;
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPhoto(url);
    }
  };
  const handleOathAccept = () => {
    setIsGenerating(true);
    // Simulate generation time
    setTimeout(() => {
      setIsGenerating(false);
      setStep('reveal');
    }, 3000);
  };
  // The CSS-built Card Frame
  const GeneratedCard = () => {
    const isFemale = gender === 'female';
    const floralColor = isFemale ? 'from-[#8B0000]/20 to-[#D4AF37]/20' : 'from-[#0B0F19] to-[#131B2B]';
    const floralAccent = isFemale ? 'text-[#8B0000]' : 'text-[#131B2B]';
    return <div className="relative w-full max-w-md mx-auto aspect-[4/5] bg-[#0B0F19] border-[6px] border-[#131B2B] shadow-2xl overflow-hidden flex flex-col" data-id="element-1668" data-mp="yjr75kqidhqs">
        {/* Outer Gold Border */}
        <div className="absolute inset-0 border-[3px] border-[#D4AF37] m-1 pointer-events-none z-20" data-id="element-1669" data-mp="2swgybuphscuh" />
        <div className="absolute inset-0 border border-[#D4AF37]/50 m-2 pointer-events-none z-20" data-id="element-1670" data-mp="2zzozovs98p59" />

        {/* Header */}
        <div className="relative z-20 pt-4 pb-2 text-center bg-[#0B0F19]" data-id="element-1671" data-mp="2qy7krgql2ti5">
          <div className="flex justify-center mb-1" data-id="element-1672" data-mp="2r0a79qsj24i4">
            <svg className="w-6 h-6 text-[#D4AF37]" viewBox="0 0 24 24" fill="currentColor" data-id="element-1673" data-mp="2uz16c3gjzw7v">
              <path d="M12 2L15 8H9L12 2Z" data-id="element-1674" data-mp="2h6ba767b9ax1" />
              <path d="M6 10H18V22H6V10Z" data-id="element-1675" data-mp="2lze4xhntqqet" />
              <path d="M12 12C13.1046 12 14 12.8954 14 14C14 15.1046 13.1046 16 12 16C10.8954 16 10 15.1046 10 14C10 12.8954 10.8954 12 12 12Z" fill="#0B0F19" data-id="element-1676" data-mp="2m0fh5pgktiy4" />
            </svg>
          </div>
          <h3 className="text-[#D4AF37] font-serif text-sm tracking-[0.2em] uppercase" data-id="element-1677" data-mp="1y9s8v32r0rb1">
            Lahore Treasury
          </h3>
          <p className="text-[#D4AF37]/60 text-[8px] tracking-[0.3em] uppercase mt-0.5" data-id="element-1678" data-mp="237le9tw89ha9">
            Enter the Lahore Court
          </p>
        </div>

        {/* Main Content Area with Floral Side Panels */}
        <div className="relative flex-1 flex mx-3 mb-3 border-2 border-[#D4AF37] bg-[#0B0F19]" data-id="element-1679" data-mp="1ep1n67frxyoy">
          {/* Left Floral Panel */}
          <div className={`w-12 border-r-2 border-[#D4AF37] bg-gradient-to-b ${floralColor} flex flex-col items-center py-4 gap-4 overflow-hidden`} data-id="element-1680" data-mp="1sl6jl1e17oh5">
            {[...Array(5)].map((_, i) => <svg key={`l-${i}`} className={`w-6 h-6 ${floralAccent} opacity-50`} viewBox="0 0 24 24" fill="currentColor" data-id="element-1681" data-mp="1xmvv0i56245r" data-mpi={i}>
                <path d="M12 2C12 2 15 7 15 12C15 17 12 22 12 22C12 22 9 17 9 12C9 7 12 2 12 2Z" data-id="element-1682" data-mp="27cnv1rw1bphm" />
                <path d="M2 12C2 12 7 9 12 9C17 9 22 12 22 12C22 12 17 15 12 15C7 15 2 12 2 12Z" data-id="element-1683" data-mp="2lbx7cgh4dzgb" />
              </svg>)}
          </div>

          {/* Center Portrait Area with Cusped Arch */}
          <div className="relative flex-1 bg-[#131B2B] overflow-hidden" data-id="element-1684" data-mp="1e07m1lum1on8">
            {/* The Photo */}
            {photo && <img src={photo} alt="Knighted Portrait" className="absolute inset-0 w-full h-full object-cover" data-id="element-1685" data-mp="1izk844dnqror" />}

            {/* Cusped Arch SVG Overlay */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-1686" data-mp="1rt71jnue7ius">
              <path d="M0,0 L100,0 L100,100 L0,100 Z M0,100 L0,30 C10,30 15,25 20,20 C25,15 30,10 50,10 C70,10 75,15 80,20 C85,25 90,30 100,30 L100,100 Z" fill="#0B0F19" data-id="element-1687" data-mp="21nfgt9uexfb0" />
              <path d="M0,30 C10,30 15,25 20,20 C25,15 30,10 50,10 C70,10 75,15 80,20 C85,25 90,30 100,30" fill="none" stroke="#D4AF37" strokeWidth="1.5" data-id="element-1688" data-mp="2anm2hj09lll9" />
            </svg>

            {/* Name Plate */}
            <div className="absolute bottom-0 inset-x-0 bg-[#0B0F19] border-t-2 border-[#D4AF37] p-4 text-center z-30" data-id="element-1689" data-mp="8tpm3j78xqpb">
              <p className="text-[#D4AF37] text-[9px] tracking-[0.2em] uppercase mb-1" data-id="element-1690" data-mp="3zv72t2l5zwk">
                {isFemale ? 'Sardarni of the Court' : 'Sardar of the Court'}
              </p>
              <h2 className="font-serif text-2xl text-[#D4AF37] mb-2 uppercase tracking-wide" data-id="element-1691" data-mp="3r4b071v5tp15">
                Harnoor Singh
              </h2>
              <p className="text-[#D4AF37]/80 text-[10px] font-serif italic" data-id="element-1692" data-mp="mrnum7yvysww">
                A newly appointed noble of the Lahore Darbar, sworn to protect
                the empire.
              </p>
            </div>
          </div>

          {/* Right Floral Panel */}
          <div className={`w-12 border-l-2 border-[#D4AF37] bg-gradient-to-b ${floralColor} flex flex-col items-center py-4 gap-4 overflow-hidden`} data-id="element-1693" data-mp="33gte9myktl63">
            {[...Array(5)].map((_, i) => <svg key={`r-${i}`} className={`w-6 h-6 ${floralAccent} opacity-50`} viewBox="0 0 24 24" fill="currentColor" data-id="element-1694" data-mp="12e2by4j420qf" data-mpi={i}>
                <path d="M12 2C12 2 15 7 15 12C15 17 12 22 12 22C12 22 9 17 9 12C9 7 12 2 12 2Z" data-id="element-1695" data-mp="sgxkbp6hxd28" />
                <path d="M2 12C2 12 7 9 12 9C17 9 22 12 22 12C22 12 17 15 12 15C7 15 2 12 2 12Z" data-id="element-1696" data-mp="xez5qgre9ogv" />
              </svg>)}
          </div>
        </div>

        {/* Founders Edition Badge */}
        <div className="absolute -bottom-2 -left-2 w-28 h-28 bg-[#0B0F19] rounded-full border-4 border-[#131B2B] z-40 flex items-center justify-center shadow-xl" data-id="element-1697" data-mp="1q4o6ct06zmhy">
          <div className="w-[88%] h-[88%] rounded-full border border-[#D4AF37] border-dashed flex flex-col items-center justify-center p-2 text-center" data-id="element-1698" data-mp="1bdfm6zf26tc7">
            <svg className="w-5 h-5 text-[#D4AF37] mb-1" viewBox="0 0 24 24" fill="currentColor" data-id="element-1699" data-mp="242w9xjrqhm23">
              <path d="M2 20H22V22H2V20ZM4 12L7 18H17L20 12L16 14L12 8L8 14L4 12Z" data-id="element-1700" data-mp="3tl2ztjdb9hn1" />
            </svg>
            <span className="text-[#D4AF37] text-[9px] font-serif tracking-widest leading-tight" data-id="element-1701" data-mp="3olzr9oy4gtab">
              FOUNDERS'
              <br data-id="element-1702" data-mp="68iiqrxkkoes" />
              EDITION
            </span>
          </div>
        </div>
      </div>;
  };
  return <AnimatePresence data-id="element-1703" data-mp="27ghk5k3e2a6">
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-1704" data-mp="276yl1xgsvdz">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-[#070A12]/90 backdrop-blur-md" onClick={onClose} data-id="element-1705" data-mp="3amqn5nn6xpmp" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-4xl bg-[#0B0F19] border border-[#D4AF37]/30 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-1706" data-mp="3em8zhv0y4sdx">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-[#D4AF37]/15" data-id="element-1707" data-mp="q95n1hkvp7h6">
            <div className="flex items-center gap-3" data-id="element-1708" data-mp="m8lms550dnqt">
              <Shield className="w-5 h-5 text-[#D4AF37]" data-id="element-1709" data-mp="hajvwy1cck4s" />
              <h2 className="font-serif text-xl text-[#FDFBF7]" data-id="element-1710" data-mp="h7xp50m9y8nm">
                Knighting Ceremony
              </h2>
            </div>
            <button onClick={onClose} className="p-2 text-[#FDFBF7]/50 hover:text-[#FDFBF7] transition-colors" data-id="element-1711" data-mp="7bb3i9m4jdkj">
              <X className="w-5 h-5" data-id="element-1712" data-mp="3c2a55uzxhpi" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 sm:p-10" data-id="element-1713" data-mp="21p2k5t2vexph">
            {step === 'intro' && <div className="max-w-3xl mx-auto text-center space-y-10" data-id="element-1714" data-mp="1xm5z7vpuypid">
                <div className="space-y-4" data-id="element-1715" data-mp="3fl3zka91d3d3">
                  <h1 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7]" data-id="element-1716" data-mp="3kl04cbpk4ytp">
                    The Knighting of the Court
                  </h1>
                  <p className="text-[#FDFBF7]/60 text-lg max-w-xl mx-auto" data-id="element-1717" data-mp="3pczvpfrkajeo">
                    An optional rite. A permanent honour. Present yourself to
                    the Darbar and receive your official Founders' Edition
                    portrait.
                  </p>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full text-[#D4AF37] text-sm font-bold tracking-widest uppercase mt-4" data-id="element-1718" data-mp="31lcghv3nwws8">
                    <Sparkles className="w-4 h-4" data-id="element-1719" data-mp="3an2ejjs1utyz" />
                    Reward: +2,500 XP
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-8" data-id="element-1720" data-mp="kx8krl636li6">
                  <div className="w-48 aspect-[4/5] bg-[#131B2B] border border-[#D4AF37]/30 flex flex-col items-center justify-center p-4" data-id="element-1721" data-mp="pv0duiigwdny">
                    <div className="w-24 h-24 rounded-full bg-[#0B0F19] border-2 border-[#D4AF37]/20 flex items-center justify-center mb-4" data-id="element-1722" data-mp="2hyef9wnvkhui">
                      <Camera className="w-8 h-8 text-[#D4AF37]/40" data-id="element-1723" data-mp="2vwkv7xsvbx5w" />
                    </div>
                    <p className="text-[#FDFBF7]/40 text-xs uppercase tracking-widest" data-id="element-1724" data-mp="2r046b1duherr">
                      Your Photo
                    </p>
                  </div>

                  <div className="flex flex-col items-center gap-2 text-[#D4AF37]" data-id="element-1725" data-mp="1yanhedpfog0a">
                    <ArrowRight className="w-6 h-6" data-id="element-1726" data-mp="2cz1iaj3ahue7" />
                    <span className="text-[10px] uppercase tracking-widest" data-id="element-1727" data-mp="2d0mc26hs59b0">
                      Transforms Into
                    </span>
                  </div>

                  <div className="w-48 aspect-[4/5] border border-[#D4AF37]/30 relative overflow-hidden" data-id="element-1728" data-mp="1ki7ct8gvw9x9">
                    <img src="https://cdn.magicpatterns.com/uploads/mYwWxceeL8ryPc8ZuV43iM/pasted-image.png" alt="Example" className="w-full h-full object-cover" data-id="element-1729" data-mp="1fkfi66ouu9pd" />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6" data-id="element-1730" data-mp="3i742nij97uw9">
                  <button onClick={() => setStep('capture')} className="w-full sm:w-auto px-8 py-4 bg-[#D4AF37] text-[#0B0F19] font-bold uppercase tracking-widest text-sm hover:bg-[#E5C158] transition-colors" data-id="element-1731" data-mp="3m74ss0fptuj5">
                    Begin Ceremony
                  </button>
                  <button onClick={onClose} className="w-full sm:w-auto px-8 py-4 border border-[#D4AF37]/30 text-[#FDFBF7] font-bold uppercase tracking-widest text-sm hover:bg-[#131B2B] transition-colors" data-id="element-1732" data-mp="4qwma8xh6fs9">
                    Maybe Later
                  </button>
                </div>
              </div>}

            {step === 'capture' && <div className="max-w-xl mx-auto space-y-8" data-id="element-1733" data-mp="39sghjlj6x6kd">
                <div className="text-center space-y-2" data-id="element-1734" data-mp="34yly8j7hytlt">
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-1735" data-mp="300k515gcxdwm">
                    Present Yourself
                  </h2>
                  <p className="text-[#FDFBF7]/60" data-id="element-1736" data-mp="2vzrw6dbedb0v">
                    Select your title and provide a clear portrait.
                  </p>
                </div>

                <div className="space-y-4" data-id="element-1737" data-mp="3smvdf2a7gj3z">
                  <label className="text-[#D4AF37] text-xs uppercase tracking-widest" data-id="element-1738" data-mp="3nr7sx06o4e4t">
                    1. Your Title
                  </label>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1739" data-mp="3jqe2o05r6n8r">
                    <button onClick={() => setGender('male')} className={`p-4 border ${gender === 'male' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#D4AF37]/30 bg-[#131B2B]'} transition-colors text-center`} data-id="element-1740" data-mp="3eoor8jemc7k5">
                      <span className="block font-serif text-xl text-[#FDFBF7] mb-1" data-id="element-1741" data-mp="it20q5y5ngrj">
                        Sardar
                      </span>
                      <span className="text-[#FDFBF7]/50 text-xs" data-id="element-1742" data-mp="np971zugqua0">(His)</span>
                    </button>
                    <button onClick={() => setGender('female')} className={`p-4 border ${gender === 'female' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#D4AF37]/30 bg-[#131B2B]'} transition-colors text-center`} data-id="element-1743" data-mp="rquirhv9h2im">
                      <span className="block font-serif text-xl text-[#FDFBF7] mb-1" data-id="element-1744" data-mp="16g0dasj5m4k6">
                        Sardarni
                      </span>
                      <span className="text-[#FDFBF7]/50 text-xs" data-id="element-1745" data-mp="1fj1imr073rms">(Her)</span>
                    </button>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-1746" data-mp="3da45h513jinm">
                  <label className="text-[#D4AF37] text-xs uppercase tracking-widest" data-id="element-1747" data-mp="3h9mt48kay7u5">
                    2. Your Portrait
                  </label>
                  <input type="file" accept="image/*" className="hidden" ref={fileInputRef} onChange={handlePhotoUpload} data-id="element-1748" data-mp="ofo68l15h5ec" />

                  {/* Captured / uploaded preview */}
                  {photo && captureMode === 'choose' && <div className="relative w-full aspect-video border border-[#D4AF37]/30 bg-[#131B2B] overflow-hidden" data-id="element-1749" data-mp="17dqmaabpzs00">
                      <img src={photo} alt="Preview" className="w-full h-full object-cover" data-id="element-1750" data-mp="12ee2jyg5b11n" />
                      <button onClick={() => setPhoto(null)} className="absolute top-4 right-4 p-2 bg-[#0B0F19]/80 backdrop-blur text-[#FDFBF7] hover:text-red-400 rounded-full" data-id="element-1751" data-mp="1c61oxkxuhrw9">
                        <X className="w-4 h-4" data-id="element-1752" data-mp="1l3u48p3kgzjn" />
                      </button>
                      <div className="absolute bottom-3 left-3 px-2 py-1 bg-[#0B0F19]/80 backdrop-blur text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase" data-id="element-1753" data-mp="24s9t25lp8ya7">
                        Ready
                      </div>
                    </div>}

                  {/* Live camera */}
                  {captureMode === 'camera' && <div className="relative w-full aspect-video border border-[#D4AF37] bg-[#131B2B] overflow-hidden" data-id="element-1754" data-mp="1m0gyw7uw970w">
                      <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover scale-x-[-1]" data-id="element-1755" data-mp="1qsqw88yc99d7" />
                      {/* Center framing guide */}
                      <div className="absolute inset-0 pointer-events-none flex items-center justify-center" data-id="element-1756" data-mp="132mu660sthob">
                        <div className="w-40 h-40 sm:w-48 sm:h-48 rounded-full border-2 border-[#D4AF37]/60 shadow-[0_0_0_9999px_rgba(11,15,25,0.45)]" data-id="element-1757" data-mp="180ek4uhvutpv" />
                      </div>
                      <div className="absolute top-3 left-3 px-2 py-1 bg-[#0B0F19]/80 backdrop-blur text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase" data-id="element-1758" data-mp="1ilfoodn6rmun">
                        Live · Frame your face
                      </div>
                      <div className="absolute bottom-4 inset-x-0 flex items-center justify-center gap-3" data-id="element-1759" data-mp="19lsotwa3up3p">
                        <button onClick={cancelCamera} className="px-4 py-2 bg-[#0B0F19]/80 backdrop-blur border border-[#FDFBF7]/30 text-[#FDFBF7] text-[10px] uppercase tracking-widest hover:bg-[#131B2B] transition-colors" data-id="element-1760" data-mp="14m68vf2w6ur7">
                          Cancel
                        </button>
                        <button onClick={capturePhoto} className="px-6 py-3 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold transition-colors flex items-center gap-2 shadow-xl" data-id="element-1761" data-mp="ltc39br5sezn">
                          <Camera className="w-4 h-4" data-id="element-1762" data-mp="2xfe1hsgptax2" /> Capture
                        </button>
                      </div>
                    </div>}

                  {/* Choose method */}
                  {!photo && captureMode === 'choose' && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1763" data-mp="29so1sxru9sfm">
                      <button onClick={startCamera} className="aspect-video border-2 border-dashed border-[#D4AF37]/30 bg-[#131B2B] hover:border-[#D4AF37]/60 transition-colors flex flex-col items-center justify-center gap-3 group" data-id="element-1764" data-mp="2dsq4na9b8rwh">
                        <div className="w-14 h-14 rounded-full bg-[#0B0F19] flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1765" data-mp="1q46yb09tmp71">
                          <Camera className="w-6 h-6 text-[#D4AF37]" data-id="element-1766" data-mp="1v0njbllq4nfw" />
                        </div>
                        <div className="text-center" data-id="element-1767" data-mp="12jd8a4crv182">
                          <p className="text-[#FDFBF7] font-serif text-base" data-id="element-1768" data-mp="16i44m99eyeu4">
                            Use Camera
                          </p>
                          <p className="text-[#FDFBF7]/50 text-[10px] mt-0.5" data-id="element-1769" data-mp="1bfvw5265byxs">
                            Take a live portrait
                          </p>
                        </div>
                      </button>
                      <button onClick={() => fileInputRef.current?.click()} className="aspect-video border-2 border-dashed border-[#D4AF37]/30 bg-[#131B2B] hover:border-[#D4AF37]/60 transition-colors flex flex-col items-center justify-center gap-3 group" data-id="element-1770" data-mp="nse3l4f24wtr">
                        <div className="w-14 h-14 rounded-full bg-[#0B0F19] flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1771" data-mp="3lf1t0rlr96">
                          <Upload className="w-6 h-6 text-[#D4AF37]" data-id="element-1772" data-mp="4ztr2xjo14c4" />
                        </div>
                        <div className="text-center" data-id="element-1773" data-mp="3tlf8zd1sq4ck">
                          <p className="text-[#FDFBF7] font-serif text-base" data-id="element-1774" data-mp="3opq6p1ye69ck">
                            Upload Photo
                          </p>
                          <p className="text-[#FDFBF7]/50 text-[10px] mt-0.5" data-id="element-1775" data-mp="3jrewur83jyuc">
                            Clear face, good lighting
                          </p>
                        </div>
                      </button>
                    </div>}

                  {cameraError && <p className="text-[#8B0000] text-xs text-center" data-id="element-1776" data-mp="30z3y99kh6n21">
                      {cameraError}
                    </p>}

                  {/* Retake controls when photo exists */}
                  {photo && captureMode === 'choose' && <div className="flex items-center justify-center gap-3 pt-2" data-id="element-1777" data-mp="2r04x0c21htp2">
                      <button onClick={() => {
                  setPhoto(null);
                  startCamera();
                }} className="text-[#D4AF37] text-[10px] uppercase tracking-widest hover:underline flex items-center gap-1.5" data-id="element-1778" data-mp="2n0mh5up2s8t3">
                        <Camera className="w-3 h-3" data-id="element-1779" data-mp="23fvzrb94w2ct" /> Retake with Camera
                      </button>
                      <span className="text-[#FDFBF7]/20" data-id="element-1780" data-mp="28egwh0p6g3wi">·</span>
                      <button onClick={() => {
                  setPhoto(null);
                  fileInputRef.current?.click();
                }} className="text-[#D4AF37] text-[10px] uppercase tracking-widest hover:underline flex items-center gap-1.5" data-id="element-1781" data-mp="2w2qf5tr3gbjf">
                        <Upload className="w-3 h-3" data-id="element-1782" data-mp="3f1ch1aufq6oe" /> Upload Different
                      </button>
                    </div>}
                </div>

                <div className="pt-6" data-id="element-1783" data-mp="3jtvwkdconbvu">
                  <button disabled={!gender || !photo} onClick={() => setStep('oath')} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] font-bold uppercase tracking-widest text-sm hover:bg-[#E5C158] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1784" data-mp="3t62zd6whzw2b">
                    Proceed to Oath
                  </button>
                </div>
              </div>}

            {step === 'oath' && <div className="max-w-2xl mx-auto text-center space-y-12 py-10" data-id="element-1785" data-mp="366nf1byhhebu">
                {isGenerating ? <div className="space-y-8" data-id="element-1786" data-mp="3k6y203woa8rq">
                    <div className="relative w-32 h-32 mx-auto" data-id="element-1787" data-mp="3k3k1l17kk9mx">
                      <div className="absolute inset-0 border-4 border-[#D4AF37]/20 rounded-full" data-id="element-1788" data-mp="3f58qkn5jft34" />
                      <div className="absolute inset-0 border-4 border-[#D4AF37] rounded-full border-t-transparent animate-spin" data-id="element-1789" data-mp="ze4dwf3nd11i" />
                      <Shield className="absolute inset-0 m-auto w-10 h-10 text-[#D4AF37] animate-pulse" data-id="element-1790" data-mp="uiyt5jlzdsf0" />
                    </div>
                    <div className="space-y-2" data-id="element-1791" data-mp="353g6zu9y6xy7">
                      <h2 className="font-serif text-2xl text-[#D4AF37]" data-id="element-1792" data-mp="3548273zmutny">
                        Forging Your Legacy...
                      </h2>
                      <p className="text-[#FDFBF7]/60" data-id="element-1793" data-mp="3e55y0yzx3uvg">
                        The royal painters are completing your portrait.
                      </p>
                    </div>
                  </div> : <>
                    <Shield className="w-16 h-16 text-[#D4AF37] mx-auto opacity-50" data-id="element-1794" data-mp="3o0zjx8zj10pw" />
                    <div className="space-y-6" data-id="element-1795" data-mp="3su2f1ljyci87">
                      <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] leading-relaxed" data-id="element-1796" data-mp="1mznuu446bxz">
                        "I pledge my loyalty to the Lahore Darbar. I shall
                        uphold the honour of the empire, protect its treasures,
                        and stand as a guardian of its legacy."
                      </h2>
                    </div>
                    <button onClick={handleOathAccept} className="px-10 py-4 bg-[#D4AF37] text-[#0B0F19] font-bold uppercase tracking-widest text-sm hover:bg-[#E5C158] transition-colors" data-id="element-1797" data-mp="1v3vlo6rymwv6">
                      I Accept This Oath
                    </button>
                  </>}
              </div>}

            {step === 'reveal' && <div className="max-w-4xl mx-auto flex flex-col lg:flex-row items-center gap-10" data-id="element-1798" data-mp="29v4jh360noje">
                <div className="flex-1 w-full" data-id="element-1799" data-mp="253w9o60ry15p">
                  <GeneratedCard data-id="element-1800" data-mp="252l5i53f2re2" />
                </div>
                <div className="flex-1 space-y-8 text-center lg:text-left" data-id="element-1801" data-mp="205kx3nj3hcjs">
                  <div className="space-y-4" data-id="element-1802" data-mp="2xkgnzpqef8fd">
                    <div className="inline-flex items-center gap-2 text-[#D4AF37] mb-2" data-id="element-1803" data-mp="2xli96n14pbat">
                      <CheckCircle2 className="w-5 h-5" data-id="element-1804" data-mp="2stgukughn8hm" />
                      <span className="text-sm font-bold tracking-widest uppercase" data-id="element-1805" data-mp="2srw014u6bpjr">
                        Knighting Complete
                      </span>
                    </div>
                    <h2 className="font-serif text-4xl text-[#FDFBF7]" data-id="element-1806" data-mp="3gr6c12boc6te">
                      Arise, Noble of the Court
                    </h2>
                    <p className="text-[#FDFBF7]/60 text-lg" data-id="element-1807" data-mp="36yqrpycwn65c">
                      Your portrait has been added to the royal archives. You
                      have been awarded 2,500 XP for completing this rite.
                    </p>
                  </div>

                  <div className="flex flex-col gap-4" data-id="element-1808" data-mp="2xw7tjrk2dhpf">
                    <button onClick={() => setStep('share')} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] font-bold uppercase tracking-widest text-sm hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2" data-id="element-1809" data-mp="2jzul17xicqn3">
                      <Download className="w-4 h-4" data-id="element-1810" data-mp="m7qddxpsgyrc" /> Save & Share Portrait
                    </button>
                    <button onClick={() => {
                  onComplete(photo!);
                  onClose();
                }} className="w-full py-4 border border-[#D4AF37]/30 text-[#FDFBF7] font-bold uppercase tracking-widest text-sm hover:bg-[#131B2B] transition-colors" data-id="element-1811" data-mp="26rvk7cdxwv0t">
                      Return to Dashboard
                    </button>
                  </div>
                </div>
              </div>}

            {step === 'share' && <div className="max-w-md mx-auto space-y-8 text-center" data-id="element-1812" data-mp="2zfrbu2onjwqq">
                <GeneratedCard data-id="element-1813" data-mp="2zeychms0w9im" />
                <div className="grid grid-cols-2 gap-4" data-id="element-1814" data-mp="2ugn137m2xqya">
                  <button className="p-4 border border-[#D4AF37]/30 bg-[#131B2B] hover:bg-[#D4AF37]/10 transition-colors flex flex-col items-center gap-2 text-[#FDFBF7]" data-id="element-1815" data-mp="2uh6afzxnz70p">
                    <Download className="w-5 h-5 text-[#D4AF37]" data-id="element-1816" data-mp="103ow93j5fmic" />
                    <span className="text-xs uppercase tracking-widest" data-id="element-1817" data-mp="f6xvl6aoj1d0">
                      Download
                    </span>
                  </button>
                  <button className="p-4 border border-[#D4AF37]/30 bg-[#131B2B] hover:bg-[#D4AF37]/10 transition-colors flex flex-col items-center gap-2 text-[#FDFBF7]" data-id="element-1818" data-mp="j6zwveci5srr">
                    <Share2 className="w-5 h-5 text-[#D4AF37]" data-id="element-1819" data-mp="sym6x7d3tlsx" />
                    <span className="text-xs uppercase tracking-widest" data-id="element-1820" data-mp="3sdhfwtxsg7mn">
                      Share
                    </span>
                  </button>
                </div>
                <button onClick={() => {
              onComplete(photo!);
              onClose();
            }} className="text-[#D4AF37] text-sm uppercase tracking-widest hover:underline" data-id="element-1821" data-mp="brkw60n2edc">
                  Back to Dashboard
                </button>
              </div>}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
};